// V157.7-ABSOLUTE: Global fetch deduplication
const LOG_FETCH_DEBUG = false; // V163.6.8: Disable debug - deduplicator now working properly

/**
 * Browser fetch rejects transport failures with TypeError. Keep this matcher
 * deliberately narrow so application errors and HTTP responses are unaffected.
 */
export function isNetworkFetchError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;

  const message = error.message.trim().toLowerCase();
  return (
    message === 'failed to fetch' ||
    message === 'load failed' ||
    message === 'network request failed' ||
    message === 'the internet connection appears to be offline.'
  );
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return String(input);
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request && input.method) {
    return input.method.toUpperCase();
  }
  return 'GET';
}

function headerValue(headers: HeadersInit | undefined, name: string): string | null {
  if (!headers) return null;
  const lower = name.toLowerCase();
  if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.get(name);
  if (Array.isArray(headers)) {
    const found = headers.find(([key]) => key.toLowerCase() === lower);
    return found ? String(found[1]) : null;
  }
  const record = headers as Record<string, string>;
  const key = Object.keys(record).find((entry) => entry.toLowerCase() === lower);
  return key ? String(record[key]) : null;
}

function hasAuthorization(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (headerValue(init?.headers, 'authorization')) return true;
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return Boolean(input.headers.get('authorization'));
  }
  return false;
}

type CachedResponse = {
  response: unknown;
  timestamp: number;
  status: number;
  statusText: string;
  contentType: string;
};

type InFlightEntry = {
  promise: Promise<Response>;
  generation: number;
};

class GlobalFetchDeduplicator {
  private inFlightRequests = new Map<string, InFlightEntry>();
  private cache = new Map<string, CachedResponse>();
  private requestGeneration = 0;
  private readonly CACHE_TTL = 300000; // 5 minutes
  
  constructor() {
    this.interceptFetch();
  }
  
  private interceptFetch() {
    const originalFetch = window.fetch;
    
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = requestUrl(input);
      const method = requestMethod(input, init);
      
      // V258.8: CRITICAL - Bypass deduplication for Cloudinary URLs
      // Cloudinary handles versioning via /vXXXXXXXXXX/ in URL path
      // App-level deduplication can serve stale cached images after uploads
      // Cloudinary's CDN handles caching correctly, so we should fetch directly
      const isCloudinary = typeof url === 'string' && (
        url.includes('cloudinary.com') || 
        url.includes('res.cloudinary.com')
      );
      
      if (isCloudinary) {
        console.log('🔄 V258.8: Cloudinary URL - bypassing deduplication');
        console.log('🔄 V258.8: URL:', url.substring(0, 120) + '...');
        // Fetch directly without any deduplication or caching
        return originalFetch(input, init);
      }

      // V167: ULTRA-AGGRESSIVE - Bypass ALL Supabase REST API calls
      // The previous specific check for Bar-Ilan queries was too brittle and could miss
      // valid requests due to encoding or query param ordering. During the Bar-Ilan
      // rollout we need absolute reliability, so we bypass ALL Supabase REST traffic.
      // This ensures auth headers, Accept headers, and query params are never altered
      // or served from a stale cached 403.
      const isSupabaseRestAPI = typeof url === 'string' && (
        url.includes('.supabase.co/rest/v1/')
      );

      // V323-DEBUG: Step 3 - Log whether Supabase calls are bypassed
      if (typeof url === 'string' && url.includes('chats')) {
        console.log('🔍 V323-DEBUG Step 3: globalFetchDedup intercepted URL =', url.substring(0, 200));
        console.log('🔍 V323-DEBUG Step 3: isSupabaseRestAPI =', isSupabaseRestAPI);
        console.log('🔍 V323-DEBUG Step 3: bypassed =', isSupabaseRestAPI);
      }

      if (isSupabaseRestAPI) {
        console.log('🔄 V167: Supabase REST API - BYPASSING ALL DEDUPLICATION');
        console.log('🔄 V167: URL:', url.substring(0, 150));
        // Bypass ALL deduplication for ALL Supabase REST queries
        // This prevents ANY interference with auth headers, Accept headers, or query params
        return originalFetch(input, init);
      }

      if (
        url.includes('.supabase.co') ||
        method !== 'GET' ||
        init?.body != null ||
        hasAuthorization(input, init)
      ) {
        return originalFetch(input, init);
      }

      const key = `${method}:${url}`;
      
      // V163.6.8: Block problematic external API calls first
      if (typeof url === 'string') {
        // Block WhatsApp API calls that cause 404s
        if (url.includes('api.whatsapp.com')) {
          if (LOG_FETCH_DEBUG) console.log(`🚫 Blocked WhatsApp API call: ${url}`);
          return Promise.reject(new Error('WhatsApp API call blocked to prevent 404 errors'));
        }

        // Block external manifest requests
        if (url.includes('manifest.json') && !url.startsWith(window.location.origin)) {
          if (LOG_FETCH_DEBUG) console.log(`🚫 Blocked external manifest request: ${url}`);
          return Promise.reject(new Error('External manifest request blocked'));
        }
      }

      // V217: Cache images globally (jpg, jpeg, png, webp, gif, svg, cloudinary)
      const isImageUrl = /\.(jpg|jpeg|png|webp|gif|svg)(\?|$)/i.test(url) || 
                         url.includes('cloudinary.com') ||
                         url.includes('res.cloudinary.com');
      
      // V163.6.8: Intercept Supabase calls AND image URLs for deduplication
      if (!url.includes('.supabase.co/') && !isImageUrl) {
        return originalFetch(input, init);
      }
      
      // V217: For images, use longer cache TTL (30 minutes)
      const imageCacheTTL = 30 * 60 * 1000; // 30 minutes for images
      const effectiveTTL = isImageUrl ? imageCacheTTL : this.CACHE_TTL;
      
      if (LOG_FETCH_DEBUG) console.log('🌐 V157.7-ABSOLUTE: Intercepted fetch:', url.substring(0, 100));
      
      // Check cache - V217: use effective TTL for images vs API calls
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.timestamp < effectiveTTL) {
        if (LOG_FETCH_DEBUG) console.log('💾 V217: Returning cached response (image or API)');
        return Promise.resolve(new Response(
          JSON.stringify(cached.response),
          {
            status: cached.status,
            statusText: cached.statusText,
            headers: { 'content-type': cached.contentType }
          }
        ));
      }
      
      // Check in-flight
      const inFlight = this.inFlightRequests.get(key);
      if (inFlight) {
        if (LOG_FETCH_DEBUG) console.log('⏳ V217: Waiting for in-flight request');
        const response = await inFlight.promise;
        return response.clone();
      }
      
      // Execute new request
      if (LOG_FETCH_DEBUG) console.log('🚀 V157.7-ABSOLUTE: Executing new request');
      
      // Non-GET, including HEAD, already returned through originalFetch.
      let hasMinimalPrefer = false;
      
      if (init?.headers) {
        if (init.headers instanceof Headers) {
          hasMinimalPrefer = init.headers.get('Prefer')?.includes('return=minimal') || false;
        } else if (Array.isArray(init.headers)) {
          hasMinimalPrefer = init.headers.some(([key, value]) => 
            key.toLowerCase() === 'prefer' && String(value).includes('return=minimal')
          );
        } else if (typeof init.headers === 'object') {
          // Check both 'Prefer' and 'prefer' keys
          const preferHeader = init.headers['Prefer'] || init.headers['prefer'];
          hasMinimalPrefer = preferHeader ? String(preferHeader).includes('return=minimal') : false;
        }
      }
      
      const generation = ++this.requestGeneration;
      const promise = originalFetch(input, init)
        .then(async (response) => {
          // 🚀 V166.40.9: Only cache and parse JSON if response has content
          if (!hasMinimalPrefer) {
            try {
              // Check if response has content before parsing
              const contentType = response.headers.get('content-type') || '';
              const contentLength = response.headers.get('content-length');
              
              // Only parse JSON if content-type indicates JSON and there's content
              if (
                  response.ok &&
                  contentType.includes('application/json') &&
                  (contentLength === null || parseInt(contentLength) > 0)
                ) {
                const clone = response.clone();
                const text = await clone.text();
                
                // Only parse if there's actual content
                if (text.trim()) {
                  try {
                    const data = JSON.parse(text);
                    this.cache.set(key, {
                      response: data,
                      timestamp: Date.now(),
                      status: response.status,
                      statusText: response.statusText,
                      contentType
                    });
                  } catch (parseError) {
                    // If parsing fails, don't cache but don't throw
                    if (LOG_FETCH_DEBUG) console.warn('⚠️ V166.40.9: Failed to parse JSON response:', parseError);
                  }
                }
              }
            } catch (error) {
              // Don't throw error for empty responses - just don't cache
              if (LOG_FETCH_DEBUG) console.warn('⚠️ V166.40.9: Could not cache response (likely empty):', error);
            }
          }
          return response;
        })
        .catch((error: unknown) => {
          // V600: Evict a rejected transport promise immediately. The existing
          // finally block still performs normal in-flight cleanup for all outcomes.
          if (isNetworkFetchError(error)) {
            const current = this.inFlightRequests.get(key);
            if (current?.generation === generation) this.inFlightRequests.delete(key);
          }
          throw error;
        })
        .finally(() => {
          // V167.FINAL: Always clear inflight so waiters don't stall 10s on error/reject
          const current = this.inFlightRequests.get(key);
          if (current?.generation === generation) this.inFlightRequests.delete(key);
        });
      
      this.inFlightRequests.set(key, { promise, generation });
      return promise;
    };
    
    if (LOG_FETCH_DEBUG) console.log('✅ V157.7-ABSOLUTE: Global fetch interceptor installed');
  }
  
  /** Drops successful response replays. In-flight requests and generations stay intact. */
  public clearCachedResponses() {
    this.cache.clear();
  }

  // Cleanup method for cache management
  public cleanup() {
    const now = Date.now();
    for (const [key, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }

  // V167: Clear cached Bar-Ilan errors
  public clearBarIlanCache() {
    console.log('🧹 V167: Clearing Bar-Ilan cached errors');
    const keysToDelete: string[] = [];
    for (const [key] of this.cache.entries()) {
      if (key.includes('founding_100_applications') ||
          (key.includes('users') && key.includes('trust_score'))) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`🧹 V167: Cleared ${keysToDelete.length} cached entries`);
  }
}

// Install immediately
export const globalFetchDeduplicator = new GlobalFetchDeduplicator();

// Cleanup every 5 minutes
setInterval(() => {
  globalFetchDeduplicator.cleanup();
}, 300000);

// V167: Export function to clear Bar-Ilan cache
export function clearBarIlanCache() {
  globalFetchDeduplicator.clearBarIlanCache();
}

export function clearCachedResponses() {
  globalFetchDeduplicator.clearCachedResponses();
}
